import { type StateCreator } from 'zustand';
import { FeaturesFilter, MultimediaFilter } from '@ev-tech/search-modules-api';

import { DEFAULT_PREFERENCES } from './preferencesSlice';
import { usePromptStore } from '../usePromptStore';
import { type MiddlewareType, type SearchStore } from '../SearchStoreProvider';

export const FEATURES = [
  FeaturesFilter.hasAirConditioning,
  FeaturesFilter.hasBalcony,
  FeaturesFilter.hasBasement,
  FeaturesFilter.hasBuiltInKitchen,
  FeaturesFilter.hasElevator,
  FeaturesFilter.hasGarage,
  FeaturesFilter.hasGarden,
  FeaturesFilter.hasPatio,
  FeaturesFilter.hasSeaOrLakeView,
  FeaturesFilter.hasSecuritySystem,
  FeaturesFilter.hasSwimmingPool,
  FeaturesFilter.hasTennisCourt,
  FeaturesFilter.hasTerrace,
  FeaturesFilter.hasWaterfront,
  FeaturesFilter.hasDoorman,
  FeaturesFilter.hasHelipad,
] as const;

export const MULTIMEDIA = [
  MultimediaFilter.hasFloorplan,
  MultimediaFilter.hasVtourNormalized,
  MultimediaFilter.hasVideoNormalized,
] as const;

// Reason: hasPriceUpdated is URL/state-only (NS-12655); keep it out of FEATURES so it is not rendered in FeaturesSelector without a translation key.
const initialFeaturesState = {
  ...Object.fromEntries(FEATURES.map((feature) => [feature, false])),
  [FeaturesFilter.hasPriceUpdated]: false,
} as Record<FeaturesFilter, boolean>;

const initialMultimediaState = Object.fromEntries(MULTIMEDIA.map((feature) => [feature, false])) as Record<MultimediaFilter, boolean>;

export type FeaturesState = {
  features: Record<FeaturesFilter, boolean>;
  multimedia: Record<MultimediaFilter, boolean>;
};

type FeaturesActions = {
  setFeature: (feature: FeaturesFilter, value: boolean) => void;
  setMultimedia: (feature: MultimediaFilter, value: boolean) => void;
};

export type FeaturesStore = FeaturesState & FeaturesActions;

export const defaultFeaturesState: Readonly<FeaturesState> = {
  features: initialFeaturesState,
  multimedia: initialMultimediaState,
};

export const createFeaturesSlice: StateCreator<SearchStore, MiddlewareType, [], FeaturesStore> = (set) => ({
  ...defaultFeaturesState,
  setFeature: (feature: FeaturesFilter, value: boolean) => {
    set(
      (state) => {
        state.features[feature] = value;
        state.page = 1;
        state.preferences = DEFAULT_PREFERENCES;
      },
      undefined,
      'setFeature',
    );
    usePromptStore.getState().setPromptValue('');
  },
  setMultimedia: (feature: MultimediaFilter, value: boolean) => {
    set(
      (state) => {
        state.multimedia[feature] = value;
        state.page = 1;
        state.preferences = DEFAULT_PREFERENCES;
      },
      undefined,
      'setMultimedia',
    );
    usePromptStore.getState().setPromptValue('');
  },
});
